import { createFileRoute, notFound } from '@tanstack/react-router';
import { z } from 'zod';
import { FeatureFlag, RouteName, TextKey } from '@mews-commander/enums';
import { checkEnterprisePrivilege } from '@mews-commander/legacy-entities';
import {
    fetchFormDetails,
    RoutingRuleAddForm,
    RoutingRuleFormWrapper,
} from '@mews-commander/modules/src/modules/routingRules';
import { RoutingRulesStepsView } from '@mews-commander/modules/src/modules/routingRulesNew';
import { OptimusProvider } from '@mews-commander/ui';
import { MewsTeam, RouteType } from '@mews/enums';
import { useFeatureFlag, useUUID } from '@mews/framework';

export const Route = createFileRoute('/_authenticated/$currentEnterpriseId/RoutingRule/Create')({
    validateSearch: z.object({
        EnterpriseId: z.string().uuid(),
    }),
    loaderDeps: ({ search: { EnterpriseId } }) => ({ EnterpriseId }),
    loader: async ({
        deps: { EnterpriseId },
        context: {
            dispatch__DO_NOT_USE_OR_YOU_WILL_BE_FIRED: dispatch,
            getState__DO_NOT_USE_OR_YOU_WILL_BE_FIRED: getState,
            featureFlagService,
            localizeText,
        },
    }) => {
        const isNewRoutingRulesOnAddEnabled = featureFlagService.hasFlag(FeatureFlag.newRoutingRulesOnAddEnabled);
        if (!isNewRoutingRulesOnAddEnabled) {
            await fetchFormDetails({ dispatch, getState, featureFlagService }, {});
        }

        const state = getState();
        if (!checkEnterprisePrivilege(state, { enterpriseId: EnterpriseId })) {
            throw notFound();
        }

        if (isNewRoutingRulesOnAddEnabled) {
            return {
                title: () => localizeText({ textKey: TextKey.NewBillingAutomation }),
            };
        }
    },
    component: Component,
    staticData: {
        title: TextKey.RoutingRule,
        routeName: RouteName.RoutingRuleCreate,
        owner: MewsTeam.Billing,
        type: RouteType.Frontend,
    },
});

function Component() {
    const { EnterpriseId: enterpriseId } = Route.useSearch();
    const isNewRoutingRulesOnAddEnabled = useFeatureFlag(FeatureFlag.newRoutingRulesOnAddEnabled);
    const id = useUUID();

    return isNewRoutingRulesOnAddEnabled ? (
        <OptimusProvider density="spacious">
            <RoutingRulesStepsView />
        </OptimusProvider>
    ) : (
        <RoutingRuleFormWrapper enterpriseId={enterpriseId}>
            <RoutingRuleAddForm enterpriseId={enterpriseId} form={`routing-rule-${id}`} />
        </RoutingRuleFormWrapper>
    );
}
